/**
 * RunKeepRunningPanel — NC-1567, NC-1580.
 *
 * Admin switch "Über Nacht laufen lassen (KeepRunning)": exempts the run's VMs
 * from the 19:00 evening shutdown (rb-nc-shutdown-evening skips VMs tagged
 * KeepRunning=true). The BE (`runs-keeprunning-toggle`) tags/untags every VM of
 * the run and persists `run.keepRunning`, so VMs created later by 130 get the
 * tag too.
 *
 * NC-1580: the switch shows what the runbook will ACTUALLY do — the VM tags,
 * read via GET …/keep-running/status — not only the run-doc flag. The two can
 * differ (tag set by hand, partially failed toggle, recreated VM); live example
 * 2026-10-07: run E511308A ran overnight with every VM tagged while the switch
 * said "aus". A drift is shown, and an admin can align the doc in one click.
 *
 * Operators see the state read-only — keeping VMs up overnight is a cost
 * decision; viewers do not get the panel at all (the parent decides).
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import { runsApi, extractErrorMessage } from '../services/api';
import { KeepRunningStatusResult } from '../services/types';

export const KEEP_RUNNING_LABEL = 'Über Nacht laufen lassen (KeepRunning)';

interface RunKeepRunningPanelProps {
  runId: string;
  /** Current persisted opt-out (absent/false = VMs stop at 19:00 like all others). */
  keepRunning?: boolean;
  /** admin only — operators see the state read-only. */
  canEdit: boolean;
  /** Re-fetch the run so the parent's copy matches what was just persisted. */
  onChanged?: () => void;
}

function vmStateLabel(keepRunning: boolean | null): string {
  if (keepRunning === null) return 'unbekannt';
  return keepRunning ? 'läuft weiter' : 'wird gestoppt';
}

export function RunKeepRunningPanel({ runId, keepRunning, canEdit, onChanged }: RunKeepRunningPanelProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [status, setStatus] = useState<KeepRunningStatusResult | null>(null);
  const [statusFailed, setStatusFailed] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      setStatus(await runsApi.getKeepRunningStatus(runId));
      setStatusFailed(false);
    } catch {
      // Fall back to the doc flag — but say so instead of pretending it is live.
      setStatus(null);
      setStatusFailed(true);
    }
  }, [runId]);

  // Re-read the tags whenever the persisted flag changes (e.g. after a toggle).
  useEffect(() => {
    loadStatus();
  }, [loadStatus, keepRunning]);

  // The VMs are the truth: the runbook reads their tags, not the run doc.
  const enabled = status ? status.effective === 'on' : keepRunning === true;
  const taggedCount = status ? status.vms.filter((v) => v.keepRunning === true).length : 0;

  const apply = async (next: boolean) => {
    // Guard the handler, not just the control: `disabled` is a rendering hint.
    if (!canEdit || saving) return;
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      const result = await runsApi.setKeepRunning(runId, next);
      if (result.vmCount === 0) {
        setInfo('Der Lauf hat noch keine VMs — der Tag wird bei der Provisionierung gesetzt.');
      }
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      // A partial failure leaves the doc unchanged — re-sync both views either way.
      onChanged?.();
      await loadStatus();
      setSaving(false);
    }
  };

  let driftText: string | null = null;
  if (status?.drift) {
    if (status.effective === 'mixed') {
      driftText = `Nur ${taggedCount} von ${status.vms.length} VMs sind vom 19:00-Shutdown ausgenommen — die übrigen werden heruntergefahren.`;
    } else if (status.effective === 'on') {
      driftText = 'Alle VMs sind vom 19:00-Shutdown ausgenommen, im Schulungsdatensatz ist das aber nicht hinterlegt.';
    } else {
      driftText = 'Im Schulungsdatensatz steht „über Nacht laufen lassen“, an den VMs fehlt die Markierung — sie werden um 19:00 heruntergefahren.';
    }
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Abend-Shutdown
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                disabled={!canEdit || saving}
                onChange={(e) => apply(e.target.checked)}
              />
            }
            label={KEEP_RUNNING_LABEL}
          />
          {saving && <CircularProgress size={18} />}
        </Box>

        <Typography variant="body2" color="text.secondary">
          {enabled
            ? 'Die VMs dieses Laufs werden vom 19:00-Shutdown ausgenommen und laufen über Nacht weiter — das verursacht Kosten.'
            : 'Die VMs werden wie alle anderen um 19:00 heruntergefahren.'}
        </Typography>

        {status && driftText && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>Abweichung:</strong> {driftText}
            {status.effective === 'mixed' && (
              <Box component="span" display="block" sx={{ mt: 0.5 }}>
                {status.vms.map((v) => `${v.vmName}: ${vmStateLabel(v.keepRunning)}`).join(' · ')}
              </Box>
            )}
            {canEdit && status.effective === 'mixed' && (
              <Box component="span" display="block" sx={{ mt: 0.5 }}>
                Den Schalter betätigen, um alle VMs einheitlich zu setzen.
              </Box>
            )}
            {canEdit && status.effective !== 'mixed' && (
              <Box sx={{ mt: 1 }}>
                <Button size="small" onClick={() => apply(status.effective === 'on')} disabled={saving}>
                  Datensatz angleichen
                </Button>
              </Box>
            )}
          </Alert>
        )}

        {statusFailed && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Der Live-Zustand der VMs konnte nicht gelesen werden — angezeigt wird der gespeicherte Wert.
          </Typography>
        )}

        {!canEdit && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Nur Administratoren können diese Einstellung ändern.
          </Typography>
        )}

        {info && (
          <Alert severity="info" sx={{ mt: 2 }} onClose={() => setInfo(null)}>
            {info}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

export default RunKeepRunningPanel;
